import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Chip, LinearProgress, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  decodePaymentToken,
  resolveRedirectPath,
  PAYMENT_MODULE_CONFIG,
} from 'src/common/utils/paymentUtils';

const REDIRECT_DELAY = 5;

export default function PaymentCompletion() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(REDIRECT_DELAY);

  const token = searchParams.get('token');
  const decoded = decodePaymentToken(token);
  const moduleConfig = decoded?.module ? PAYMENT_MODULE_CONFIG[decoded.module] : null;
  const redirectPath = resolveRedirectPath(decoded, moduleConfig);
  const isDetailRedirect = !!decoded?.redirectTo;
  const isRenewal = !!decoded?.forRenewal;
  const message = isRenewal
    ? moduleConfig?.renewalMessage ?? 'Your licenses have been renewed successfully.'
    : moduleConfig?.successMessage ?? 'Payment completed successfully.';
  const redirectLabel = moduleConfig?.redirectLabel ?? 'Go to Dashboard';
  const redirectDisplayLabel = moduleConfig?.label ?? 'Dashboard';

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          navigate(redirectPath);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [navigate, redirectPath]);

  const progress = ((REDIRECT_DELAY - countdown) / REDIRECT_DELAY) * 100;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        px: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 420,
          width: '100%',
          p: { xs: 4, sm: 5 },
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2.5,
        }}
      >
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.success.main, 0.12)
                : alpha(theme.palette.success.main, 0.2),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: (theme) =>
              `0 0 0 12px ${alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.06 : 0.08)}`,
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main' }} />
        </Box>

        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Payment Successful
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {message}
          </Typography>
        </Box>

        {decoded?.orderId && (
          <Chip
            label={`Order ID: ${decoded.orderId}`}
            size="small"
            variant="outlined"
            sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary', borderColor: 'divider' }}
          />
        )}

        <Box sx={{ width: '100%' }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            color="success"
            sx={{
              borderRadius: 2,
              height: 5,
              bgcolor: (theme) =>
                alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.12 : 0.2),
            }}
          />
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
            Redirecting to {redirectDisplayLabel} in {countdown}s…
          </Typography>
        </Box>

        {!isDetailRedirect && (
          <Button
            variant="contained"
            color="success"
            disableElevation
            fullWidth
            onClick={() => navigate(redirectPath)}
            sx={{ py: 1.25, borderRadius: 2, fontWeight: 600, fontSize: 15 }}
          >
            {redirectLabel}
          </Button>
        )}
      </Paper>
    </Box>
  );
}
